import { StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { mergeOptions, mergeProps } from '@/utils/merge';
import { FrisbiiPayment } from 'base/components/standalone/FrisbiiPayment/FrisbiiPayment';
import Script from 'next/script';
import { ComponentProps, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useFrisbiiPayment } from '@/hooks/useFrisbiiPayment';
import useUserAuth from '@/hooks/useUserAuth';
import { tryGetWindowProp } from '@/utils/tryGetWindowProp';

export interface StandaloneFrisbiiPaymentProps extends StandaloneComponentProps<typeof FrisbiiPayment> {
  button?: ComponentProps<typeof FrisbiiPayment.Button>;
  handleSuccess?: (successResponse: { redirect_to: string }) => Promise<any>;
  handleFailure?: (message?: string, error?: any) => Promise<any>;
  autoTrigger?: boolean;
  caption?: ReactNode;
}

export const StandaloneFrisbiiPayment: StandaloneComponent<StandaloneFrisbiiPaymentProps> = ({
  button,
  handleSuccess,
  handleFailure,
  autoTrigger,
  caption,
  ...props
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasAutoTriggered, setHasAutoTriggered] = useState(false);
  const { frisbiiSession, onAcceptEvent, onCancelEvent, onErrorFrisbiiEvent, createFrisbiiSession } =
    useFrisbiiPayment();
  const { isUserLoggedIn } = useUserAuth();
  const activeSessionRef = useRef<{ signature: string; customer: string; subscription: string } | null>(null);

  const openFrisbiiPurchaseWindow = useCallback(
    async (session = frisbiiSession) => {
      if (!session) {
        console.warn('Frisbii session is not available');
        return;
      }

      const { sessionId, signature, customer, subscription } = session;
      if (!sessionId || !signature) {
        throw new Error('Frisbii session is missing sessionId or signature');
      }

      try {
        const Reepay = await tryGetWindowProp<typeof window.Reepay>('Reepay');

        const isMobileDevice = window.innerWidth < 768;

        if (isMobileDevice) {
          new Reepay.WindowSubscription(sessionId);
          return;
        }

        const modal = new Reepay.ModalSubscription(sessionId, { showReceipt: false });
        if (customer && subscription) {
          activeSessionRef.current = { signature, customer, subscription };
        }

        modal.addEventHandler(Reepay.Event.Accept, async (data) => {
          activeSessionRef.current = null;
          try {
            const acceptResponse = await onAcceptEvent({
              from: window.location.href,
              signature: signature,
              ...data,
            });
            handleSuccess?.(acceptResponse);
          } catch (error) {
            console.error('Error during payment acceptance:', error);
            await handleFailure?.();
          } finally {
            modal.destroy();
            setIsProcessing(false);
          }
        });

        modal.addEventHandler(Reepay.Event.Cancel, async (data) => {
          activeSessionRef.current = null;
          try {
            await onCancelEvent({ from: window.location.href, signature, ...data });
            handleFailure?.('Betalning avbruten av användaren');
          } catch (error) {
            console.error('Error during payment cancellation:', error);
          } finally {
            modal.destroy();
            setIsProcessing(false);
          }
        });

        modal.addEventHandler(Reepay.Event.Error, async (data) => {
          activeSessionRef.current = null;
          try {
            await onCancelEvent({ from: window.location.href, signature, ...data });
            await onErrorFrisbiiEvent(data);
            await handleFailure?.('Betalning avbruten');
          } catch (error) {
            console.error('Error during payment error handling:', error);
          } finally {
            modal.destroy();
            setIsProcessing(false);
          }
        });
      } catch (error) {
        console.error('Error starting Reepay payment:', error);
        await handleFailure?.('Förlåt, ett fel har uppstått. Vänligen försök igen om en stund.');
      }
    },
    [frisbiiSession, onAcceptEvent, onCancelEvent, isProcessing, handleSuccess, handleFailure],
  );

  const startPurchase = useCallback(async () => {
    setIsProcessing(true);
    try {
      let sessionToUse = frisbiiSession;
      if (isUserLoggedIn && !frisbiiSession) {
        sessionToUse = await createFrisbiiSession();
      }

      await openFrisbiiPurchaseWindow(sessionToUse);
    } catch (error) {
      console.error('Error starting purchase process:', error);
    }
    setIsProcessing(false);
  }, [isUserLoggedIn, frisbiiSession]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const session = activeSessionRef.current;
      if (session) {
        const blob = new Blob([JSON.stringify({ ...session, from: window.location.href })], {
          type: 'application/json',
        });
        navigator.sendBeacon('/api/payments/v2/cancel-frisbii-session', blob);
        activeSessionRef.current = null;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      handleBeforeUnload();
    };
  }, []);

  useEffect(() => {
    if (autoTrigger && !hasAutoTriggered && !isProcessing) {
      setHasAutoTriggered(true);
      startPurchase();
    }
  }, [autoTrigger, hasAutoTriggered, isProcessing, openFrisbiiPurchaseWindow]);

  return (
    <FrisbiiPayment {...props}>
      <Script
        key="frisbii-payment"
        id="checkout-script"
        src="https://checkout.reepay.com/checkout.js"
        onError={(error) => {
          console.error('Error loading Reepay checkout script:', error);
          handleFailure?.('Kunde inte ladda betalningssystemet. Vänligen uppdatera sidan.');
        }}
      />
      <FrisbiiPayment.Content>
        <FrisbiiPayment.Button
          {...mergeProps(button, { isLoading: isProcessing })}
          options={mergeOptions(
            {
              onClick: async (e: Event) => {
                e.preventDefault();
                await startPurchase();
              },
              disabled: isProcessing,
            },
            props.options?.$button,
          )}
        />
        {caption && <FrisbiiPayment.Caption>{caption}</FrisbiiPayment.Caption>}
      </FrisbiiPayment.Content>
    </FrisbiiPayment>
  );
};
